import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { DetailRow } from '@/components/DetailRow'
import { TrafficLightDot } from '@/components/PostureIndicator'
import { ThreatDownDetectionsCard } from '@/components/threatdown/ThreatDownDetectionsCard'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { TRAFFIC } from '@/lib/trafficLight'

function FieldsCard({ title, fields = [] }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const visible = fields.filter((f) => f.value != null && f.value !== '')
  if (!visible.length) return null
  return (
    <CollapsePanel
      id={`td-fields-${id}`}
      title={title}
      collapsedSummary={`${visible.length} field(s)`}
      open={open}
      onOpenChange={setOpen}
      bodyClassName="space-y-2 text-sm"
    >
      {visible.map((field) => (
        <DetailRow key={field.label} label={field.label} value={field.value} />
      ))}
    </CollapsePanel>
  )
}

function CollapsibleTableCard({ title, summary, children }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  return (
    <CollapsePanel
      id={`td-table-${id}`}
      title={title}
      collapsedSummary={summary}
      open={open}
      onOpenChange={setOpen}
    >
      {children}
    </CollapsePanel>
  )
}

function ModuleStatusBadge({ status }) {
  if (!status) return <span className="text-muted-foreground">—</span>
  const text = status.toLowerCase()
  if (text === 'started') return <Badge variant="success">{status}</Badge>
  if (text === 'stopped' || text === 'failed') return <Badge variant="destructive">{status}</Badge>
  return <Badge variant="warning">{status}</Badge>
}

function PostureSectionCard({ section }) {
  const traffic = TRAFFIC[section.status] ?? TRAFFIC.grey
  const issues = Array.isArray(section.issues) ? section.issues.filter(Boolean) : []
  return (
    <div
      className={
        section.status === 'red'
          ? 'rounded-lg border border-destructive/40 bg-destructive/5 p-3'
          : section.status === 'amber'
            ? 'rounded-lg border border-amber-500/30 bg-amber-500/5 p-3'
            : section.status === 'green'
              ? 'rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3'
              : 'rounded-lg border border-border p-3'
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <TrafficLightDot status={section.status} className="h-2.5 w-2.5" />
        <span className="text-sm font-medium">{section.title}</span>
        <Badge variant={traffic.badge} className="h-5 px-1.5">
          {traffic.label}
        </Badge>
      </div>
      {issues.length > 0 ? (
        <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
          {issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">No issues flagged</p>
      )}
    </div>
  )
}

function PostureAttentionBanner({ overallStatus, sections = [], issues = [] }) {
  if (overallStatus !== 'amber' && overallStatus !== 'red') return null

  const sectionIssues = sections.flatMap((section) =>
    (Array.isArray(section.issues) ? section.issues : [])
      .filter(Boolean)
      .map((issue) => ({
        section: section.title,
        status: section.status,
        issue,
      })),
  )
  const fallbackIssues = Array.isArray(issues)
    ? issues.filter(Boolean).map((issue) => ({ section: null, status: overallStatus, issue }))
    : []
  const rows = sectionIssues.length > 0 ? sectionIssues : fallbackIssues
  if (rows.length === 0) return null

  const overall = TRAFFIC[overallStatus] ?? TRAFFIC.grey
  const heading =
    overallStatus === 'red' ? 'Why this endpoint needs action' : 'Why this endpoint needs review'

  return (
    <div
      className={
        overallStatus === 'red'
          ? 'mt-4 rounded-lg border border-destructive/40 bg-destructive/5 p-3'
          : 'mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3'
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <TrafficLightDot status={overallStatus} className="h-2.5 w-2.5" />
        <p className="text-sm font-medium">{heading}</p>
        <Badge variant={overall.badge}>{overall.label}</Badge>
      </div>
      <ul className="mt-2 space-y-1.5 text-sm">
        {rows.map((row) => (
          <li key={`${row.section || 'overall'}-${row.issue}`} className="break-words">
            {row.section ? (
              <>
                <span className="font-medium">{row.section}: </span>
                {row.issue}
              </>
            ) : (
              row.issue
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function ThreatDownDetailView({ endpoint }) {
  if (!endpoint) return null

  const overall = TRAFFIC[endpoint.overall_status] ?? TRAFFIC.grey
  const flags = endpoint.flags ?? {}
  const postureSections = Array.isArray(endpoint.posture_sections)
    ? endpoint.posture_sections
    : []

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-xl">{endpoint.hostname}</CardTitle>
                <TrafficLightDot status={endpoint.overall_status} className="h-3 w-3" />
                <Badge variant={overall.badge}>{overall.label}</Badge>
              </div>
              {endpoint.display_name && endpoint.display_name !== endpoint.hostname && (
                <p className="text-sm text-muted-foreground">{endpoint.display_name}</p>
              )}
              {endpoint.customer_name && (
                <p className="text-sm text-muted-foreground">{endpoint.customer_name}</p>
              )}
            </div>
            {endpoint.agent_id && (
              <Link
                to={`/agents/${endpoint.agent_id}?tab=security`}
                className="text-sm text-primary hover:underline"
              >
                Open device (Security tab)
              </Link>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {postureSections.map((section) => (
              <PostureSectionCard key={section.id} section={section} />
            ))}
          </div>
          <PostureAttentionBanner
            overallStatus={endpoint.overall_status}
            sections={postureSections}
            issues={endpoint.issues}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            {flags.isolated && <Badge variant="destructive">Isolated</Badge>}
            {flags.reboot_required && <Badge variant="warning">Reboot required</Badge>}
            {(flags.infection_count ?? 0) > 0 && (
              <Badge variant="destructive">
                {flags.infection_count} infection{flags.infection_count === 1 ? '' : 's'}
              </Badge>
            )}
            {(flags.suspicious_activity_count ?? 0) > 0 && (
              <Badge variant="warning">
                {flags.suspicious_activity_count} suspicious{' '}
                {flags.suspicious_activity_count === 1 ? 'activity' : 'activities'}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <FieldsCard title="Protection" fields={endpoint.protection_fields} />
        <FieldsCard title="Endpoint Detection & Response" fields={endpoint.edr_fields} />
        <FieldsCard title="Scans & risk" fields={endpoint.scan_fields} />
        <FieldsCard title="Network" fields={endpoint.network_fields} />
        <FieldsCard title="Identity" fields={endpoint.identity_fields} />
        <FieldsCard title="Check-in location" fields={endpoint.location_fields} />
        <FieldsCard title="Policy & asset" fields={endpoint.policy_fields} />
      </div>

      {endpoint.protection_modules?.length > 0 && (
        <CollapsibleTableCard
          title="Protection modules"
          summary={`${endpoint.protection_modules.length} module(s)`}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Module</th>
                  <th className="pb-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {endpoint.protection_modules.map((mod) => (
                  <tr key={mod.name} className="border-b border-border/60 last:border-0">
                    <td className="py-2 pr-4">{mod.name}</td>
                    <td className="py-2">
                      <ModuleStatusBadge status={mod.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleTableCard>
      )}

      {endpoint.nics?.length > 0 && (
        <CollapsibleTableCard
          title="Network adapters"
          summary={`${endpoint.nics.length} adapter(s)`}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Adapter</th>
                  <th className="pb-2 pr-4 font-medium">MAC</th>
                  <th className="pb-2 font-medium">IP addresses</th>
                </tr>
              </thead>
              <tbody>
                {endpoint.nics.map((nic, index) => (
                  <tr key={`${nic.mac_address}-${index}`} className="border-b border-border/60 last:border-0">
                    <td className="py-2 pr-4">{nic.description || '—'}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{nic.mac_address || '—'}</td>
                    <td className="py-2">{nic.ips?.length ? nic.ips.join(', ') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleTableCard>
      )}

      {endpoint.drives?.length > 0 && (
        <CollapsibleTableCard
          title="Drive encryption"
          summary={`${endpoint.drives.length} drive(s)`}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Drive</th>
                  <th className="pb-2 pr-4 font-medium">Status</th>
                  <th className="pb-2 pr-4 font-medium">Method</th>
                  <th className="pb-2 font-medium">Protector</th>
                </tr>
              </thead>
              <tbody>
                {endpoint.drives.map((drive) => (
                  <tr key={drive.label} className="border-b border-border/60 last:border-0">
                    <td className="py-2 pr-4">{drive.label || '—'}</td>
                    <td className="py-2 pr-4 capitalize">{drive.status || '—'}</td>
                    <td className="py-2 pr-4">{drive.encryption_method || '—'}</td>
                    <td className="py-2">{drive.protector_type || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleTableCard>
      )}

      {endpoint.tags?.length > 0 && (
        <CollapsibleTableCard title="Tags" summary={`${endpoint.tags.length} tag(s)`}>
          <div className="flex flex-wrap gap-2">
            {endpoint.tags.map((tag) => (
              <Badge key={tag} variant="secondary">
                {tag}
              </Badge>
            ))}
          </div>
        </CollapsibleTableCard>
      )}

      <ThreatDownDetectionsCard endpointId={endpoint.id} />
    </div>
  )
}
